// src/pages/league-dashboard/CommissionerDraftSetup.js
// Commissioner-only draft configuration page
import React, { useState, useEffect } from 'react';
import { Settings, Clock, AlertCircle, Check } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import { parseIntOrPrev, parseIntOrNull } from '../../services/formUtils';

const CommissionerDraftSetup = ({ leagueId, league, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  
  // Draft mode: 'live' or 'offline'
  const [draftMode, setDraftMode] = useState('offline');
  
  // Live draft settings (for live drafts only)
  const [liveDraftSettings, setLiveDraftSettings] = useState({
    timer_seconds: 90,
    timer_mode: 'auto_pick', // 'auto_pick', 'manual_skip', 'none'
    grace_period_seconds: 30,
    auto_skip_inactive: true,
    allow_pause: true,
    pick_order_locked: false
  });
  
  // General draft configuration (applies to both live and offline)
  const [draftConfig, setDraftConfig] = useState({
    draft_rounds: 23,
    draft_year: null, // Will be set from league current_season
    // Dynasty league settings
    dynasty_initial_draft_type: 'snake', // 'snake', 'random_repeating', 'auction', 'commissioner'
    dynasty_subsequent_draft_type: 'standings_lottery', // 'basic_standings', 'standings_lottery', 'auction', 'commissioner'
    // Redraft league settings
    redraft_initial_draft_type: 'snake', // 'snake', 'random_repeating', 'auction', 'commissioner'
    // Lottery settings (for dynasty leagues with standings_lottery)
    lottery_enabled: false,
    lottery_top_excluded: 3, // 0-10
    lottery_weighting: 'nba_style',
    lottery_num_picks: 3,
    // Keeper settings (for dynasty leagues)
    keeper_deadline_date: null, // YYYY-MM-DD format
    keeper_positions_locked: false
  });

  useEffect(() => {
    loadDraftSettings();
  }, [leagueId]);

  const loadDraftSettings = async () => {
    try {
      setLoading(true);
      const leagueData = await leaguesAPI.getLeagueDetails(leagueId);
      
      if (leagueData && leagueData.success && leagueData.league) {
        const leagueInfo = leagueData.league;
        
        // Get draft_mode from league.
        // String `||` (not `??`): empty draft_mode treated as missing → 'offline' default.
        setDraftMode(leagueInfo.draft_mode || 'offline');

        // Get draft_settings from league_settings JSONB (for live drafts).
        const leagueSettings = leagueInfo.league_settings ?? {};
        const settings = leagueSettings.draft_settings ?? {};

        setLiveDraftSettings({
          timer_seconds: settings.timer_seconds ?? 90,
          // String `||` (not `??`): empty timer_mode string treated as missing.
          timer_mode: settings.timer_mode || 'auto_pick',
          grace_period_seconds: settings.grace_period_seconds ?? 30,
          auto_skip_inactive: settings.auto_skip_inactive !== false,
          allow_pause: settings.allow_pause !== false,
          pick_order_locked: settings.pick_order_locked ?? false
        });

        // Get general draft configuration from league_settings.
        // 8m.8a: ?? for numeric/boolean defaults (preserves 0-keystone for
        // lottery_top_excluded per 8m.5c.api-response precedent). String
        // defaults retain `||` with inline rationale.
        setDraftConfig({
          draft_rounds: leagueSettings.draft_rounds ?? 23,
          draft_year: leagueInfo.draft_year,
          // String `||` (not `??`) on 3 lines: empty draft-type strings treated as missing.
          dynasty_initial_draft_type: leagueSettings.dynasty_initial_draft_type || 'snake',
          dynasty_subsequent_draft_type: leagueSettings.dynasty_subsequent_draft_type || 'standings_lottery',
          redraft_initial_draft_type: leagueSettings.redraft_initial_draft_type || 'snake',
          lottery_enabled: leagueSettings.lottery_enabled ?? false,
          lottery_top_excluded: leagueSettings.lottery_top_excluded ?? 3,  // 0-keystone preserved
          // String `||`: empty lottery_weighting string treated as missing.
          lottery_weighting: leagueSettings.lottery_weighting || 'nba_style',
          lottery_num_picks: leagueSettings.lottery_num_picks ?? 3,
          // String/date `||`: empty keeper_deadline_date string treated as missing → null canonical.
          keeper_deadline_date: leagueSettings.keeper_deadline_date || null,
          keeper_positions_locked: leagueSettings.keeper_positions_locked ?? false
        });
      }
    } catch (error) {
      console.error('Error loading draft settings:', error);
      setError('Failed to load draft settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');

      // 8m.10a null-validation gates (per 8m.6a-revised + 8m.5a discipline).
      // Null checks fire BEFORE any other logic (and before API call) to avoid
      // Number(null) === 0 falling through bounds errors per 8m.5a precedent.
      // JSX gates match the field's render conditions: live-mode fields
      // validated only when draftMode === 'live'; lottery fields only when
      // draftConfig.lottery_enabled === true. draft_year + keeper_deadline_date
      // omitted intentionally — both null-canonical (backend infers current
      // season; null = no deadline set).
      if (draftConfig.draft_rounds === null || draftConfig.draft_rounds === undefined) {
        setError('Draft rounds is required');
        setSaving(false);
        return;
      }
      if (draftMode === 'live' &&
          (liveDraftSettings.timer_seconds === null || liveDraftSettings.timer_seconds === undefined)) {
        setError('Timer seconds is required for live draft');
        setSaving(false);
        return;
      }
      if (draftMode === 'live' &&
          (liveDraftSettings.grace_period_seconds === null || liveDraftSettings.grace_period_seconds === undefined)) {
        setError('Grace period seconds is required for live draft');
        setSaving(false);
        return;
      }
      if (draftConfig.lottery_enabled === true &&
          (draftConfig.lottery_num_picks === null || draftConfig.lottery_num_picks === undefined)) {
        setError('Lottery number of picks is required when lottery is enabled');
        setSaving(false);
        return;
      }
      if (draftConfig.lottery_enabled === true &&
          (draftConfig.lottery_top_excluded === null || draftConfig.lottery_top_excluded === undefined)) {
        setError('Lottery top excluded is required when lottery is enabled');
        setSaving(false);
        return;
      }

      // Update draft_mode and all draft configuration
      const settingsUpdate = {
        draft_mode: draftMode,
        draft_settings: draftMode === 'live' ? liveDraftSettings : null,
        // General draft configuration (always included)
        draft_rounds: draftConfig.draft_rounds,
        draft_year: draftConfig.draft_year,  // 8m.8a: was silently dropped on save (same class as 8m.6a-revised promotion_cost omission). Now persisted.
        dynasty_initial_draft_type: draftConfig.dynasty_initial_draft_type,
        dynasty_subsequent_draft_type: draftConfig.dynasty_subsequent_draft_type,
        redraft_initial_draft_type: draftConfig.redraft_initial_draft_type,
        lottery_enabled: draftConfig.lottery_enabled,
        lottery_top_excluded: draftConfig.lottery_top_excluded,
        lottery_weighting: draftConfig.lottery_weighting,
        lottery_num_picks: draftConfig.lottery_num_picks,
        keeper_deadline_date: draftConfig.keeper_deadline_date,
        keeper_positions_locked: draftConfig.keeper_positions_locked
      };
      
      const response = await leaguesAPI.updateLeagueSettings(leagueId, settingsUpdate);
      
      if (response && response.success) {
        setMessage('Draft settings saved successfully');
        setTimeout(() => setMessage(''), 3000);
        
        if (onUpdate) {
          onUpdate();
        }
      } else {
        // String `||` (not `??`): empty error-message string treated as missing.
        throw new Error(response?.message || 'Failed to save draft settings');
      }
    } catch (error) {
      console.error('Error saving draft settings:', error);
      // String `||` chain (not `??`): empty error-message strings treated as missing through fallback chain.
      setError(error.response?.data?.detail || error.message || 'Failed to save draft settings');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={dynastyTheme.components.page}>
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 border-2 border-t-transparent animate-spin rounded-full ${dynastyTheme.classes.border.primary}`} />
            <span className={`${dynastyTheme.classes.text.white} text-lg`}>Loading draft settings...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Messages */}
      {message && (
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.success}/20 text-green-400`}>
          <Check className="inline w-5 h-5 mr-2" />
          {message}
        </div>
      )}
      
      {error && (
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.error}/20 text-red-400`}>
          <AlertCircle className="inline w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {/* Draft Mode Selection */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h2 className={`${dynastyTheme.components.heading.h2} mb-4`}>Draft Mode</h2>
          <p className={`${dynastyTheme.classes.text.neutralLight} mb-4`}>
            Choose how your league will conduct the draft
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setDraftMode('offline')}
              className={`p-6 rounded-lg border-2 transition-all ${
                draftMode === 'offline'
                  ? `${dynastyTheme.classes.border.primary} ${dynastyTheme.classes.bg.primary}/10`
                  : `${dynastyTheme.classes.border.neutralLight} ${dynastyTheme.classes.bg.darkLighter}`
              }`}
            >
              <h3 className={`${dynastyTheme.components.heading.h3} mb-2`}>Offline Draft</h3>
              <p className={dynastyTheme.classes.text.neutralLight}>
                Teams make picks asynchronously. No timer or real-time coordination required.
              </p>
            </button>
            
            <button
              onClick={() => setDraftMode('live')}
              className={`p-6 rounded-lg border-2 transition-all ${
                draftMode === 'live'
                  ? `${dynastyTheme.classes.border.primary} ${dynastyTheme.classes.bg.primary}/10`
                  : `${dynastyTheme.classes.border.neutralLight} ${dynastyTheme.classes.bg.darkLighter}`
              }`}
            >
              <h3 className={`${dynastyTheme.components.heading.h3} mb-2`}>Live Draft</h3>
              <p className={dynastyTheme.classes.text.neutralLight}>
                Real-time draft room with WebSocket connectivity, timers, and chat.
              </p>
            </button>
          </div>
        </div>
      </div>

      {/* Live Draft Settings (only show if live mode selected) */}
      {draftMode === 'live' && (
        <div className={dynastyTheme.components.card.base}>
          <div className="p-6">
            <h2 className={`${dynastyTheme.components.heading.h2} mb-4 flex items-center gap-2`}>
              <Clock className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
              Live Draft Settings
            </h2>
            
            <div className="space-y-6">
              {/* Timer Settings */}
              <div>
                <label className="flex items-center space-x-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={liveDraftSettings.timer_seconds === 0}
                    onChange={(e) => setLiveDraftSettings({
                      ...liveDraftSettings,
                      timer_seconds: e.target.checked ? 0 : 90,
                      timer_mode: e.target.checked ? 'no_timer' : 'auto_pick'
                    })}
                    className="w-5 h-5 rounded"
                  />
                  <span className={dynastyTheme.classes.text.white}>
                    No time limit per pick
                  </span>
                </label>

                {liveDraftSettings.timer_seconds === 0 ? (
                  <p className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>
                    Picks are untimed. Each owner takes as long as they need, or the commissioner can force/skip a pick.
                  </p>
                ) : (
                  <>
                    <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2`}>
                      Pick Timer (seconds)
                    </label>
                    <input
                      type="number"
                      min="30"
                      max="300"
                      step="10"
                      value={liveDraftSettings.timer_seconds}
                      onChange={(e) => setLiveDraftSettings({
                        ...liveDraftSettings,
                        timer_seconds: parseIntOrPrev(e.target.value, liveDraftSettings.timer_seconds)
                      })}
                      className={dynastyTheme.components.input}
                    />
                    <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
                      Time limit for each pick (30-300 seconds)
                    </p>
                  </>
                )}
              </div>

              {/* Timer Mode (only show when timer is enabled) */}
              {liveDraftSettings.timer_seconds > 0 && (
                <div>
                  <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2`}>
                    Timer Behavior
                  </label>
                  <select
                    value={liveDraftSettings.timer_mode}
                    onChange={(e) => setLiveDraftSettings({
                      ...liveDraftSettings,
                      timer_mode: e.target.value
                    })}
                    className={dynastyTheme.components.input}
                  >
                    <option value="auto_pick">Auto-Pick (Best Available)</option>
                    <option value="manual_skip">Manual Skip (Commissioner Action Required)</option>
                    <option value="none">No Auto-Action (Timer Only)</option>
                  </select>
                  <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
                    What happens when the timer expires
                  </p>
                </div>
              )}

              {/* Grace Period */}
              {liveDraftSettings.timer_seconds > 0 && liveDraftSettings.timer_mode === 'auto_pick' && (
                <div>
                  <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2`}>
                    Disconnect Grace Period (seconds)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="120"
                    step="5"
                    value={liveDraftSettings.grace_period_seconds}
                    onChange={(e) => setLiveDraftSettings({
                      ...liveDraftSettings,
                      grace_period_seconds: parseIntOrPrev(e.target.value, liveDraftSettings.grace_period_seconds)
                    })}
                    className={dynastyTheme.components.input}
                  />
                  <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
                    How long to wait for a disconnected team before auto-picking (0 = auto-pick immediately)
                  </p>
                </div>
              )}

              {/* Additional Options */}
              <div className="space-y-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={liveDraftSettings.auto_skip_inactive}
                    onChange={(e) => setLiveDraftSettings({
                      ...liveDraftSettings,
                      auto_skip_inactive: e.target.checked
                    })}
                    className="w-5 h-5 rounded"
                  />
                  <span className={dynastyTheme.classes.text.white}>
                    Auto-skip inactive teams
                  </span>
                </label>
                
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={liveDraftSettings.allow_pause}
                    onChange={(e) => setLiveDraftSettings({
                      ...liveDraftSettings,
                      allow_pause: e.target.checked
                    })}
                    className="w-5 h-5 rounded"
                  />
                  <span className={dynastyTheme.classes.text.white}>
                    Allow draft pause/resume
                  </span>
                </label>
                
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={liveDraftSettings.pick_order_locked}
                    onChange={(e) => setLiveDraftSettings({
                      ...liveDraftSettings,
                      pick_order_locked: e.target.checked
                    })}
                    className="w-5 h-5 rounded"
                  />
                  <span className={dynastyTheme.classes.text.white}>
                    Lock draft order (prevent trades during draft)
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* General Draft Configuration */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h2 className={`${dynastyTheme.components.heading.h2} mb-4`}>
            General Draft Configuration
          </h2>
          
          <div className="space-y-6">
            {/* Draft Rounds */}
            <div>
              <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2`}>
                Draft Rounds
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={draftConfig.draft_rounds}
                onChange={(e) => setDraftConfig({
                  ...draftConfig,
                  draft_rounds: parseIntOrPrev(e.target.value, draftConfig.draft_rounds)
                })}
                className={dynastyTheme.components.input}
              />
              <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
                Number of rounds in the draft
              </p>
            </div>

            {/* Draft Year */}
            <div>
              <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2`}>
                Draft Year
              </label>
              <input
                type="number"
                min="2020"
                max="2100"
                value={draftConfig.draft_year || ''}
                onChange={(e) => setDraftConfig({
                  ...draftConfig,
                  draft_year: parseIntOrNull(e.target.value)
                })}
                className={dynastyTheme.components.input}
              />
              <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
                Year for this draft (usually current season or next season)
              </p>
            </div>

            {/* League Type Specific Settings */}
            {league?.league_settings?.league_type === 'dynasty' ? (
              <>
                {/* Dynasty Initial Draft Type */}
                <div>
                  <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2`}>
                    Year 1 Draft Type
                  </label>
                  <select
                    value={draftConfig.dynasty_initial_draft_type}
                    onChange={(e) => setDraftConfig({
                      ...draftConfig,
                      dynasty_initial_draft_type: e.target.value
                    })}
                    className={dynastyTheme.components.input}
                  >
                    <option value="snake">Snake Draft</option>
                    <option value="random_repeating">Random (Same Order)</option>
                    <option value="auction">Auction</option>
                    <option value="commissioner">Commissioner Sets Order</option>
                  </select>
                </div>

                {/* Dynasty Subsequent Draft Type */}
                <div>
                  <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2`}>
                    Year 2+ Draft Type
                  </label>
                  <select
                    value={draftConfig.dynasty_subsequent_draft_type}
                    onChange={(e) => setDraftConfig({
                      ...draftConfig,
                      dynasty_subsequent_draft_type: e.target.value,
                      lottery_enabled: e.target.value === 'standings_lottery'
                    })}
                    className={dynastyTheme.components.input}
                  >
                    <option value="basic_standings">Standings-Based</option>
                    <option value="standings_lottery">Standings with Lottery</option>
                    <option value="auction">Auction</option>
                    <option value="commissioner">Commissioner Sets Order</option>
                  </select>
                </div>

                {/* Lottery Settings (only if standings_lottery) */}
                {draftConfig.dynasty_subsequent_draft_type === 'standings_lottery' && (
                  <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-lg space-y-4`}>
                    <h3 className={`${dynastyTheme.components.heading.h3} mb-3`}>Lottery Settings</h3>
                    
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={draftConfig.lottery_enabled}
                        onChange={(e) => setDraftConfig({
                          ...draftConfig,
                          lottery_enabled: e.target.checked
                        })}
                        className="w-5 h-5 rounded"
                      />
                      <span className={dynastyTheme.classes.text.white}>
                        Enable Lottery
                      </span>
                    </label>

                    {draftConfig.lottery_enabled && (
                      <>
                        <div>
                          <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2`}>
                            Top Teams Excluded from Lottery
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            value={draftConfig.lottery_top_excluded}
                            onChange={(e) => setDraftConfig({
                              ...draftConfig,
                              lottery_top_excluded: parseIntOrPrev(e.target.value, draftConfig.lottery_top_excluded)
                            })}
                            className={dynastyTheme.components.input}
                          />
                          <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
                            Number of top teams excluded from lottery (0-10)
                          </p>
                        </div>

                        <div>
                          <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2`}>
                            Lottery Weighting
                          </label>
                          <select
                            value={draftConfig.lottery_weighting}
                            onChange={(e) => setDraftConfig({
                              ...draftConfig,
                              lottery_weighting: e.target.value
                            })}
                            className={dynastyTheme.components.input}
                          >
                            <option value="nba_style">NBA Style</option>
                          </select>
                        </div>

                        <div>
                          <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2`}>
                            Number of Lottery Picks
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={draftConfig.lottery_num_picks}
                            onChange={(e) => setDraftConfig({
                              ...draftConfig,
                              lottery_num_picks: parseIntOrPrev(e.target.value, draftConfig.lottery_num_picks)
                            })}
                            className={dynastyTheme.components.input}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Keeper Settings */}
                <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-lg space-y-4`}>
                  <h3 className={`${dynastyTheme.components.heading.h3} mb-3`}>Keeper Settings</h3>
                  
                  <div>
                    <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2`}>
                      Keeper Deadline Date
                    </label>
                    <input
                      type="date"
                      value={draftConfig.keeper_deadline_date || ''}
                      onChange={(e) => setDraftConfig({
                        ...draftConfig,
                        // String `||` (not `??`): empty-string-as-null canonical for "no deadline set" (date field, not numeric).
                        keeper_deadline_date: e.target.value || null
                      })}
                      className={dynastyTheme.components.input}
                    />
                    <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
                      Deadline for teams to submit keeper selections
                    </p>
                  </div>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={draftConfig.keeper_positions_locked}
                      onChange={(e) => setDraftConfig({
                        ...draftConfig,
                        keeper_positions_locked: e.target.checked
                      })}
                      className="w-5 h-5 rounded"
                    />
                    <span className={dynastyTheme.classes.text.white}>
                      Lock keeper positions (prevent position changes after deadline)
                    </span>
                  </label>
                </div>
              </>
            ) : (
              /* Redraft League Settings */
              <div>
                <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2`}>
                  Draft Type
                </label>
                <select
                  value={draftConfig.redraft_initial_draft_type}
                  onChange={(e) => setDraftConfig({
                    ...draftConfig,
                    redraft_initial_draft_type: e.target.value
                  })}
                  className={dynastyTheme.components.input}
                >
                  <option value="snake">Snake Draft</option>
                  <option value="random_repeating">Random (Same Order)</option>
                  <option value="auction">Auction</option>
                  <option value="commissioner">Commissioner Sets Order</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className={`${dynastyTheme.components.button.primary} px-6 py-3 flex items-center gap-2`}
          >
            <Settings className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save Draft Settings'}
          </button>
        </div>
      </div>

    </div>
  );
};

export default CommissionerDraftSetup;

